import { SoundByte } from "../enum/sounds";
import MonoBehavior from "../lib/MonoBehavior";
import { Time } from "../lib/Time";
import { Physics } from "../math/Physics";
import Vector2 from "../math/Vector2";
import Color from "../util/Color";
import Explosion from "./Explosion";
import SoundManager from "./SoundManager";
import SPBogey from "./SPBogey";
import SmartBogey from "./SPEnemies/SmartBogey";
import SPPlayer from "./SPPlayer";
import Trail from "./Trail";

enum State {
    Searching,
    LockedOn
}

export default class SPRocketBehavior extends MonoBehavior {
    state: State = State.Searching;
    velocity = Vector2.zero;
    acceleration = Vector2.zero;
    accelerationForce = 300;
    friction = 0;
    lifetime = 10;
    explosionSize = 64;
    damage = 5;
    scanRange = 512;
    scanFOV = 90;
    target: SPBogey|SPPlayer|undefined;
    trail: Trail|undefined;
    isPlayerRocket = true;

    Init(): void {
        this.trail = this.gameObject.AddScript(Trail);
    }

    Die(playersInRange: (SPBogey|SPPlayer)[]) {
        this.engine.NewGameObjectWithScript(Explosion, {
            position: this.transform.position,
            initialValues: {
                size: this.explosionSize,
                w: this.explosionSize * 2,
                color: new Color(255,255,80)
            }
        });
        SoundManager.Singleton.PlaySound(SoundByte.Explosion);

        for (const ent of playersInRange) {
            ent.Damage(this.damage);
        }

        this.engine.RemoveGameObject(this.gameObject);
    }

    SearchingUpdate() {
        const sensorLeftT = this.transform.rotation.angle - this.scanFOV/2*Math.PI/180;
        const sensorRightT = this.transform.rotation.angle + this.scanFOV/2*Math.PI/180;

        const validPlayers: (SPBogey|SPPlayer)[] = this.engine.gameObjects.filter(
            v => this.isPlayerRocket ? v.GetScript(SPBogey) : v.GetScript(SPPlayer)
        ).map(v => this.isPlayerRocket ? v.GetScript(SPBogey) : v.GetScript(SPPlayer));

        let closest:SPBogey|SPPlayer|undefined, closestDist = Number.MAX_SAFE_INTEGER;

        for (let i = sensorLeftT; i < sensorRightT; i += (sensorRightT-sensorLeftT)/1024) {
            const vec = Vector2.fromRadians(i);
            const end = this.transform.position.add(vec.scale(this.scanRange));

            for (const player of validPlayers) {
                if (player instanceof SmartBogey && player.isFriendly && this.isPlayerRocket) continue;
                if (Physics.CircleLineCollision({
                    position: player.transform.position,
                    radius: player.size
                }, {
                    start: this.transform.position,
                    end
                })) {
                    const dist = Vector2.distance(this.transform.position, player.transform.position);

                    if (dist < closestDist) {
                        closestDist = dist;
                        closest = player;
                    }
                    // this.target = player;
                    // this.state = State.LockedOn;
                    // return;
                }
            }

            if (closest) {
                this.target = closest;
                this.state = State.LockedOn;
                return;
            }
        }
    }

    LockedOnUpdate() {
        if (!this.target) return;

        if ((this.target instanceof SPPlayer && this.target.isDead) || this.target.hp <= 0) {
            this.target = undefined;
            this.state = State.Searching;
            return;
        }

        this.transform.rotation = this.velocity.normalized;
        this.acceleration = this.target.transform.position.sub(this.transform.position).normalized.scale(this.accelerationForce);
    }

    Update(): void {
        this.lifetime -= Time.deltaTime;

        

        switch (this.state) {
            case State.Searching:
                this.SearchingUpdate();
                break;
            case State.LockedOn:
                this.LockedOnUpdate();
                break;
        
            default:
                break;
        }

        const validPlayers: (SPBogey|SPPlayer)[] = this.engine.gameObjects.filter(
            v => this.isPlayerRocket ? v.GetScript(SPBogey) : v.GetScript(SPPlayer)
        ).map(v => this.isPlayerRocket ? v.GetScript(SPBogey) : v.GetScript(SPPlayer)).filter(
            v => Vector2.distance(v.transform.position, this.transform.position) <= this.explosionSize + v.size
        );

        for (const player of validPlayers) {
            if (Physics.CircleCircleCollision({
                position: player.transform.position,
                radius: player.size
            }, {
                position: this.transform.position,
                radius: 4
            }) || Vector2.distance(player.transform.position, this.transform.position) <= this.explosionSize/2) {
                this.Die(validPlayers);
                return;
            }
        }

        if (this.lifetime <= 0) {
            this.Die(validPlayers);
            return;
        }

        const friction = this.state === State.LockedOn ? 0.5 : 0;

        this.velocity = this.velocity.add(this.acceleration.scale(Time.deltaTime));
        this.velocity = this.velocity.sub(this.velocity.scale(friction * Time.deltaTime));
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));
    }

    Draw(): void {
        this.ctx.lineWidth = 2;
        this.ctx.strokeStyle = '#ffff55';
        this.engine.Line(this.transform.position.add(this.transform.forward.scale(4)), this.transform.position.add(this.transform.back.scale(4)));

        this.ctx.fillStyle = '#ffff5555';
        this.engine.FillRect(this.transform.position.add(new Vector2(-25, 12)), new Vector2(50, 4));
        this.ctx.fillStyle = '#ffff55';
        this.engine.FillRect(this.transform.position.add(new Vector2(-25, 12)), new Vector2(50 * (this.lifetime/10), 4));
    }

    DrawGizmos(): void {
        if (this.state === State.Searching) {
            const sensorLeftT = this.transform.rotation.angle - this.scanFOV/2*Math.PI/180;
            const sensorRightT = this.transform.rotation.angle + this.scanFOV/2*Math.PI/180;
            this.ctx.strokeStyle = '#ffff5510';
            this.ctx.lineWidth = 1;
            for (let i = sensorLeftT; i < sensorRightT; i += (sensorRightT-sensorLeftT)/1024) {
                const vec = Vector2.fromRadians(i);
                this.engine.Line(this.transform.position, this.transform.position.add(vec.scale(this.scanRange)));
            }
        }
    }
}